
// To parse this data:
//
//   import { Convert, JSONParse } from "./file";
//
//   const jSONParse = Convert.toJSONParse(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

export interface JSONParse {
    application: Application;
    plan:        Plan;
}

export interface Application {
    version: string;
    build?: string;
}

export interface Plan {
    buildingType: number;
    id?: string;
    sectionName?: string;
    room:         Room;
}

export interface Room {
    offset?:   Array<number[]>;
    after?:   Array<number[]>;
    captured: Array<Captured | null>;
    cameraData?: Array<CameraData[] | null>;
}

export interface CameraData {
    position: number[];
    imagePath: string;
}

export interface Captured {
    windows:  Window[];
    doors:    DoorElement[];
    walls:    Wall[];
    openings: Opening[];
    objects:  Object[];
    version:  number;
}

export interface DoorElement {
    category:         DoorCategory;
    confidence:       DoorConfidence;
    dimensions:       number[];
    completedEdges:   any[];
    parentIdentifier: string;
    identifier:       string;
    curve:            null;
    transform:        number[];
}

export interface DoorCategory {
    door: CategoryDoor;
}

export interface CategoryDoor {
    isOpen: boolean;
}

export interface DoorConfidence {
    medium?: High;
    high?:   High;
}

export interface High {
}

export interface Object {
    dimensions: number[];
    confidence: { [key: string]: High };
    transform:  number[];
    category:   { [key: string]: High };
    identifier: string;
}

// export interface ObjectCategory {
//     table?:        High;
//     chair?:        High;
//     stairs?:       High;
//     refrigerator?: High;
//     television?:   High;
//     storage?:      High;
//     bed?:          High;
//     sofa?:         High;
//     sink?:         High;
//     washerDryer?:  High;
// }

// export interface ObjectConfidence {
//     medium?: High;
//     high?:   High;
//     low?:    High;
// }

export interface Opening {
    category:         OpeningCategory;
    confidence:       OpeningConfidence;
    dimensions:       number[];
    completedEdges:   any[];
    parentIdentifier: string;
    identifier:       string;
    curve:            null;
    transform:        number[];
}

export interface OpeningCategory {
    opening: High;
}

export interface OpeningConfidence {
    high: High;
}

export interface Wall {
    category:         WallCategory;
    confidence:       OpeningConfidence;
    dimensions:       number[];
    completedEdges:   any[];
    parentIdentifier: null;
    identifier:       string;
    curve:            null;
    transform:        number[];
}

export interface WallCategory {
    wall: High;
}

export interface Window {
    category:         WindowCategory;
    confidence:       { [key: string]: High };
    dimensions:       number[];
    completedEdges:   any[];
    parentIdentifier: string;
    identifier:       string;
    curve:            null;
    transform:        number[];
}

export interface WindowCategory {
    window: High;
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toJSONParse(json: string): JSONParse {
        return cast(JSON.parse(json), r("JSONParse"));
    }

    public static jSONParseToJson(value: JSONParse): string {
        return JSON.stringify(uncast(value, r("JSONParse")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any = ''): never {
    if (key) {
        throw Error(`Invalid value for key "${key}". Expected type ${JSON.stringify(typ)} but got ${JSON.stringify(val)}`);
    }
    throw Error(`Invalid value ${JSON.stringify(val)} for type ${JSON.stringify(typ)}`, );
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) {}
        }
        return invalidValue(typs, val);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases, val);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue("array", val);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue("Date", val);
        }
        return d;
    }

    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue("object", val);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
            result[prop.key] = transform(v, prop.typ, getProps, prop.key);
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val);
    }
    if (typ === false) return invalidValue(typ, val);
    while (typeof typ === "object" && typ.ref !== undefined) {
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
            : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
            : invalidValue(typ, val);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "JSONParse": o([
        { json: "application", js: "application", typ: r("Application") },
        { json: "plan", js: "plan", typ: r("Plan") },
    ], false),
    "Application": o([
        { json: "version", js: "version", typ: "" },
        { json: "build", js: "build", typ: u(undefined, "") },
    ], false),
    "Plan": o([
        { json: "building_type", js: "buildingType", typ: 0 },
        { json: "id", js: "id", typ: u(undefined, "") },
        { json: "sectionName", js: "sectionName", typ: u(undefined, "") },
        { json: "room", js: "room", typ: r("Room") },
    ], false),
    "Room": o([
        { json: "offset", js: "offset", typ: u(undefined, a(a(3.14))) },
        { json: "after", js: "after", typ: u(undefined, a(a(3.14))) },
        { json: "captured", js: "captured", typ: a(u(r("Captured"), null)) },
        { json: "cameraData", js: "cameraData", typ: u(undefined, a(u(a(r("CameraData")), null))) },
    ], false),
    "CameraData": o([
        { json: "position", js: "position", typ: a(3.14) },
        { json: "imagePath", js: "imagePath", typ: "" },
    ], false),
    "Captured": o([
        { json: "windows", js: "windows", typ: a(r("Window")) },
        { json: "doors", js: "doors", typ: a(r("DoorElement")) },
        { json: "walls", js: "walls", typ: a(r("Wall")) },
        { json: "openings", js: "openings", typ: a(r("Opening")) },
        { json: "objects", js: "objects", typ: a(r("Object")) },
        { json: "version", js: "version", typ: 0 },
    ], false),
    "DoorElement": o([
        { json: "category", js: "category", typ: r("DoorCategory") },
        { json: "confidence", js: "confidence", typ: r("DoorConfidence") },
        { json: "dimensions", js: "dimensions", typ: a(3.14) },
        { json: "completedEdges", js: "completedEdges", typ: a("any") },
        { json: "parentIdentifier", js: "parentIdentifier", typ: "" },
        { json: "identifier", js: "identifier", typ: "" },
        { json: "curve", js: "curve", typ: null },
        { json: "transform", js: "transform", typ: a(3.14) },
    ], false),
    "DoorCategory": o([
        { json: "door", js: "door", typ: r("CategoryDoor") },
    ], false),
    "CategoryDoor": o([
        { json: "isOpen", js: "isOpen", typ: true },
    ], false),
    "DoorConfidence": o([
        { json: "medium", js: "medium", typ: u(undefined, r("High")) },
        { json: "high", js: "high", typ: u(undefined, r("High")) },
    ], false),
    "High": o([
    ], false),
    "Object": o([
        { json: "dimensions", js: "dimensions", typ: a(3.14) },
        { json: "confidence", js: "confidence", typ: m(r("High")) },
        { json: "transform", js: "transform", typ: a(3.14) },
        { json: "category", js: "category", typ: m(r("High")) },
        { json: "identifier", js: "identifier", typ: "" },
    ], false),
    // "ObjectCategory": o([
    //     { json: "table", js: "table", typ: u(undefined, r("High")) },
    //     { json: "chair", js: "chair", typ: u(undefined, r("High")) },
    //     { json: "stairs", js: "stairs", typ: u(undefined, r("High")) },
    //     { json: "refrigerator", js: "refrigerator", typ: u(undefined, r("High")) },
    //     { json: "television", js: "television", typ: u(undefined, r("High")) },
    //     { json: "storage", js: "storage", typ: u(undefined, r("High")) },
    //     { json: "bed", js: "bed", typ: u(undefined, r("High")) },
    //     { json: "sofa", js: "sofa", typ: u(undefined, r("High")) },
    //     { json: "sink", js: "sink", typ: u(undefined, r("High")) },
    //     { json: "washerDryer", js: "washerDryer", typ: u(undefined, r("High")) },
    // ], false),
    // "ObjectConfidence": o([
    //     { json: "medium", js: "medium", typ: u(undefined, r("High")) },
    //     { json: "high", js: "high", typ: u(undefined, r("High")) },
    //     { json: "low", js: "low", typ: u(undefined, r("High")) },
    // ], false),
    "Opening": o([
        { json: "category", js: "category", typ: r("OpeningCategory") },
        { json: "confidence", js: "confidence", typ: r("OpeningConfidence") },
        { json: "dimensions", js: "dimensions", typ: a(3.14) },
        { json: "completedEdges", js: "completedEdges", typ: a("any") },
        { json: "parentIdentifier", js: "parentIdentifier", typ: "" },
        { json: "identifier", js: "identifier", typ: "" },
        { json: "curve", js: "curve", typ: null },
        { json: "transform", js: "transform", typ: a(3.14) },
    ], false),
    "OpeningCategory": o([
        { json: "opening", js: "opening", typ: r("High") },
    ], false),
    "OpeningConfidence": o([
        { json: "high", js: "high", typ: r("High") },
    ], false),
    "Wall": o([
        { json: "category", js: "category", typ: r("WallCategory") },
        { json: "confidence", js: "confidence", typ: r("OpeningConfidence") },
        { json: "dimensions", js: "dimensions", typ: a(3.14) },
        { json: "completedEdges", js: "completedEdges", typ: a("any") },
        { json: "parentIdentifier", js: "parentIdentifier", typ: null },
        { json: "identifier", js: "identifier", typ: "" },
        { json: "curve", js: "curve", typ: null },
        { json: "transform", js: "transform", typ: a(3.14) },
    ], false),
    "WallCategory": o([
        { json: "wall", js: "wall", typ: r("High") },
    ], false),
    "Window": o([
        { json: "category", js: "category", typ: r("WindowCategory") },
        { json: "confidence", js: "confidence", typ: m(r("High")) },
        { json: "dimensions", js: "dimensions", typ: a(3.14) },
        { json: "completedEdges", js: "completedEdges", typ: a("any") },
        { json: "parentIdentifier", js: "parentIdentifier", typ: "" },
        { json: "identifier", js: "identifier", typ: "" },
        { json: "curve", js: "curve", typ: null },
        { json: "transform", js: "transform", typ: a(3.14) },
    ], false),
    "WindowCategory": o([
        { json: "window", js: "window", typ: r("High") },
    ], false),
};
